/**
 * KAN-578: pure logic for deleting a released CoinSing version (typed confirm, blocker preview, error copy).
 * The server (`coinsingDeleteRelease`) stays authoritative; this only drives the dialog.
 */

export interface DeleteDeviceRef {
  id: string;
  name: string;
}

export interface DeleteImpact {
  pending: DeleteDeviceRef[];
  downloading: DeleteDeviceRef[];
  runningCount: number;
}

export interface DeleteReleaseResult {
  ok: true;
  version: string;
  deleted: { storagePaths: string[]; catalog: true };
  auditWritten: boolean;
  warnings: { runningDeviceCount: number; onlyVersion: boolean };
}

export type DeleteErrorKind =
  | 'blocked' // dialog switches to blocked state (device list or text), no Delete button
  | 'retryable' // dialog stays open, commit button becomes "Retry delete"
  | 'inline' // dialog stays open, controls re-enabled, message shown
  | 'permission' // Close only
  | 'not_found'; // treated as already deleted: refresh and remove the row

export interface DeleteErrorView {
  kind: DeleteErrorKind;
  reason: string;
  message: string;
  devices: DeleteDeviceRef[];
}

export const DELETE_COPY = {
  network: "Couldn't reach the server. Nothing was changed. Check your connection and try again.",
  permission: 'You do not have permission to delete releases. Ask an administrator for the "Delete release" permission.',
  rateLimited: 'Too many delete requests. Wait a minute and try again.',
  notFound: 'This version was already deleted. The list has been refreshed.',
  mismatch: 'The version you typed does not match. Check it and try again.',
  unauthenticated: 'Your session has expired. Sign in again and retry.',
  unexpected: 'Delete failed. Nothing further was removed. Try again, or contact support if it keeps happening.',
  alreadyDeleting: 'Another delete of this version is already in progress. Refresh in a moment to see the result or retry.',
  deleteInProgress: 'Another delete of this version is already running. Try again in a minute.',
  partial: (version: string) =>
    `Delete did not finish. v${version} is marked "Deleting" and can no longer be released. Select Retry delete to finish.`,
  emptyList: 'No released versions. Publish one with publish_coinsing_release.ps1.'
};

/** Exact match after trim; case-sensitive; the version without a leading "v". */
export function isDeleteConfirmMatch(input: unknown, version: string): boolean {
  return typeof input === 'string' && version !== '' && input.trim() === version;
}

export type ConfirmHintState = 'empty' | 'mismatch' | 'match';

export function confirmHintState(input: unknown, version: string): ConfirmHintState {
  if (typeof input !== 'string' || input.trim() === '') return 'empty';
  return isDeleteConfirmMatch(input, version) ? 'match' : 'mismatch';
}

export const CONFIRM_HINT_TEXT: Record<ConfirmHintState, string> = {
  empty: 'Type the version number exactly as shown.',
  mismatch: "Doesn't match yet.",
  match: 'Matches. You can delete now.'
};

/** "Name (id)"; name falls back to the id alone. */
export function deviceLabel(d: { id: string; name?: string }): string {
  const name = (d.name || '').trim();
  return name && name !== d.id ? `${name} (${d.id})` : d.id;
}

function sameVersion(a: unknown, b: string): boolean {
  return typeof a === 'string' && a.trim() === b;
}

function normalizeRunning(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const t = raw.trim();
  const m = /^(\d+)\.(\d+)\.(\d+)(?:\.0)?$/.exec(t);
  return m ? `${m[1]}.${m[2]}.${m[3]}` : null;
}

const DOWNLOAD_WINDOW_MS = 30 * 60 * 1000;

function toMillis(v: any): number | null {
  if (!v) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  const t = v instanceof Date ? v.getTime() : new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Client preview of what the server guards check, from the already-loaded device list.
 * Soft-deleted devices are ignored. Pending = update needed and targeted at this version.
 * Downloading = coinsingUpdate.status downloading/verifying for this version updated in the last 30 minutes.
 */
export function computeDeleteImpact(version: string, devices: any[], nowMs: number = Date.now()): DeleteImpact {
  const pending: DeleteDeviceRef[] = [];
  const downloading: DeleteDeviceRef[] = [];
  let runningCount = 0;
  for (const d of devices || []) {
    if (!d || d.isDeleted === true) continue;
    const id = String(d.deviceId ?? '');
    const ref = { id, name: String(d.deviceName || d.name || id) };
    if (sameVersion(d.coinsingTargetVersion, version) && d.coinsingUpdateNeeded === true) pending.push(ref);
    const upd = d.coinsingUpdate;
    if (upd && (upd.status === 'downloading' || upd.status === 'verifying')
        && sameVersion(upd.targetVersion ?? d.coinsingTargetVersion, version)) {
      const at = toMillis(upd.updatedAt ?? upd.at);
      if (at !== null && nowMs - at <= DOWNLOAD_WINDOW_MS) downloading.push(ref);
    }
    if (normalizeRunning(d.appVersion) === version) runningCount++;
  }
  return { pending, downloading, runningCount };
}

export function blockerText(reason: 'pending_devices' | 'download_in_progress', count: number): string {
  const n = `${count} ${count === 1 ? 'device is' : 'devices are'}`;
  return reason === 'pending_devices'
    ? `${n} waiting to install this version. Re-target or clear their pending update first, then try again.`
    : `${n} downloading or verifying this version right now. Wait until they finish, or for 30 minutes after their last activity, then try again.`;
}

export function runningWarningText(version: string, n: number): string {
  return `Warning: ${n} ${n === 1 ? 'device is' : 'devices are'} running v${version}. They keep working, but you will no longer be able to roll back to this version.`;
}

export const ONLY_VERSION_WARNING =
  'Warning: This is the only released version. After deleting it there will be no version to release until you publish a new one.';

export function formatBytes(bytes: unknown): string {
  if (typeof bytes !== 'number' || !isFinite(bytes) || bytes < 0) return 'Unknown';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}

export type EnvBadge = { kind: 'prod' | 'dev' | 'other'; label: string };
export function environmentBadge(env: unknown): EnvBadge {
  const t = typeof env === 'string' ? env.trim() : '';
  if (t.toLowerCase() === 'production') return { kind: 'prod', label: 'Production' };
  if (t.toLowerCase() === 'dev') return { kind: 'dev', label: 'Dev' };
  return { kind: 'other', label: t || 'Unknown' };
}

function readDevices(raw: any, key = 'devices'): DeleteDeviceRef[] {
  const list = raw?.details?.[key];
  if (!Array.isArray(list)) return [];
  return list
    .filter((x: any) => x && (x.id !== undefined))
    .map((x: any) => ({ id: String(x.id), name: String(x.name || x.id) }));
}

/** Maps a callable rejection (contract details.reason AND Firebase codes) to the dialog view. Never exposes raw codes. */
export function mapDeleteError(err: any, version: string): DeleteErrorView {
  const rawReason = err?.details?.reason ?? err?.customData?.reason;
  const reason = typeof rawReason === 'string' ? rawReason : '';
  const code = String(err?.code || '').replace(/^functions\//, '');
  const devices = readDevices(err);
  const view = (kind: DeleteErrorKind, message: string, r: string): DeleteErrorView =>
    ({ kind, message, reason: r, devices: kind === 'blocked' ? devices : [] });

  switch (reason) {
    case 'pending_devices':
    case 'version_in_use_pending': {
      // Both guards can apply; the server lists pending first, then the downloading devices (counts may exceed the capped lists).
      const dl = readDevices(err, 'downloadingDevices');
      const pendingCount = typeof err?.details?.deviceCount === 'number' ? err.details.deviceCount : devices.length;
      const dlCount = typeof err?.details?.downloadingDeviceCount === 'number' ? err.details.downloadingDeviceCount : dl.length;
      let message = blockerText('pending_devices', pendingCount);
      if (dlCount > 0) message += ' ' + blockerText('download_in_progress', dlCount);
      const seen = new Set(devices.map(d => d.id));
      return { kind: 'blocked', reason: 'pending_devices', message, devices: [...devices, ...dl.filter(d => !seen.has(d.id))] };
    }
    case 'download_in_progress': {
      const count = typeof err?.details?.deviceCount === 'number' ? err.details.deviceCount : devices.length;
      return view('blocked', blockerText('download_in_progress', count), 'download_in_progress');
    }
    case 'version_deleting':
      return view('blocked', DELETE_COPY.alreadyDeleting, 'version_deleting');
    case 'delete_in_progress':
      return view('inline', DELETE_COPY.deleteInProgress, 'delete_in_progress');
    case 'not_found':
      return view('not_found', DELETE_COPY.notFound, 'not_found');
    case 'confirm_mismatch':
    case 'invalid_version':
      return view('inline', DELETE_COPY.mismatch, reason);
    case 'rate_limited':
      return view('inline', DELETE_COPY.rateLimited, reason);
    case 'permission_denied':
      return view('permission', DELETE_COPY.permission, reason);
    case 'storage_delete_failed':
    case 'catalog_delete_failed':
      return view('retryable', DELETE_COPY.partial(version), reason);
    case 'invalid_release':
      return view('inline', DELETE_COPY.unexpected, reason);
    default:
      break;
  }
  switch (code) {
    case 'permission-denied': return view('permission', DELETE_COPY.permission, 'permission_denied');
    case 'resource-exhausted': return view('inline', DELETE_COPY.rateLimited, 'rate_limited');
    case 'not-found': return view('not_found', DELETE_COPY.notFound, 'not_found');
    case 'invalid-argument': return view('inline', DELETE_COPY.mismatch, 'invalid_version');
    case 'unauthenticated': return view('inline', DELETE_COPY.unauthenticated, 'unauthenticated');
    case 'unavailable':
    case 'deadline-exceeded': return view('inline', DELETE_COPY.network, 'network');
    case 'internal': return view('inline', DELETE_COPY.unexpected, 'internal');
    default: break;
  }
  // No code at all (browser offline / fetch failure) is a network problem; anything else is unexpected.
  if (!code) return view('inline', DELETE_COPY.network, 'network');
  return view('inline', DELETE_COPY.unexpected, 'unknown');
}
